import { LocateFixed } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "src/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { ToastAlert } from "src/components/ui/toaster";
import { set_expertise_data } from "src/config/redux/slicer/expertise/expertiseSlicer";
import { getAddressFromCoords, getAddressLabel, DEFAULT_COORDS } from "src/config/services/fellas/google.api";
import { updateHealthCare } from "src/config/services/fellas/health-care.api";
import { getDragLocation } from "src/lib/utils";
import LeafletMapView from "src/components/map/LeafletMapView";
import LocationSearchInput, { PlaceSearchResult } from "src/components/map/LocationSearchInput";

function DialogLocationHomecare() {
  const dispatch = useDispatch();
  const { value: expertise } = useSelector((state: any) => state.Expertise);
  const homecare_area = expertise?.homecare_area || {};
  const current_address = homecare_area?.address || [];
  const radius = homecare_area?.radius || 5; // default 5km
  const coords = homecare_area?.coords && typeof homecare_area.coords.lat === 'number'
    ? homecare_area.coords
    : DEFAULT_COORDS;

  const onDragHomecareLocation = async (location: any) => {
    const { address, full_address, coords: newCoords, label } = await getDragLocation(location);
    dispatch(set_expertise_data({
      ...expertise,
      homecare_area: {
        ...homecare_area,
        coords: newCoords,
        address,
        full_address,
        label: label || homecare_area?.label || '',
        radius,
      }
    }));
  };

  const updateHomecareAreaForm = async () => {
    const { id } = expertise;
    const resp = await updateHealthCare({ id, homecare_area: JSON.stringify(homecare_area) });
    ToastAlert(resp, 'Lokasi Berhasil di update');
  };

  const getCurrentUserLocation = () => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const newCoords = { lat: latitude, lng: longitude };

          const place: any = await getAddressFromCoords(newCoords);
          const address = place.address_components || [];
          const full_address = place.formatted_address || `${latitude}, ${longitude}`;
          const label = place.name || '';
          dispatch(set_expertise_data({
            ...expertise,
            homecare_area: {
              ...homecare_area,
              coords: newCoords,
              address,
              full_address,
              label: label || homecare_area?.label || '',
              radius,
            }
          }));
        },
        (error) => {
          ToastAlert({ error }, `Error getting user location: ${error.message}`);
        }
      );
    } else {
      ToastAlert({ error: 'Error' }, 'Geolocation is not supported by your browser');
    }
  };

  useEffect(() => {
    if (!homecare_area?.label && !homecare_area?.coords) {
      getCurrentUserLocation();
    }
  }, []);

  return (
    <DialogContent className="sm:max-w-[425px] lg:min-w-[500px] bg-white">
      <DialogHeader>
        <DialogTitle className="text-center">Tentukan Pusat Lokasi Layanan HomeCare</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <Input
          placeholder="Nama Lokasi Layanan (e.g. Rumah Sakit, Klinik, Basecamp)"
          value={homecare_area?.label || ''}
          onChange={(e) => {
            dispatch(set_expertise_data({
              ...expertise,
              homecare_area: { ...homecare_area, label: e.target.value }
            }));
          }}
        />

        <div className="h-52 rounded-sm overflow-hidden border border-gray-200">
          <LeafletMapView
            center={coords}
            zoom={13}
            markerPosition={coords}
            draggable={true}
            onDragEnd={onDragHomecareLocation}
            onMapClick={onDragHomecareLocation}
            circle={{
              center: coords,
              radius: radius,
            }}
          />
        </div>

        <div className="flex gap-2 items-end">
          <div className="w-1/3">
            <p className="mb-1 text-xs text-gray-600">Jangkauan (km)</p>
            <Input
              placeholder="in km"
              type="number"
              value={radius}
              onChange={(e) =>
                dispatch(set_expertise_data({
                  ...expertise,
                  homecare_area: { ...homecare_area, radius: Number(e.target.value) }
                }))
              }
            />
          </div>
          <div className="w-full">
            <p className="mb-1 text-xs text-gray-600">Cari alamat lokasi HomeCare</p>
            <LocationSearchInput
              placeholder={homecare_area?.full_address || "Cari alamat lokasi..."}
              currentCoords={coords}
              onPlaceSelected={(place: PlaceSearchResult) => {
                const { address_components: address, full_address, coords: newCoords, name: label } = place;
                dispatch(set_expertise_data({
                  ...expertise,
                  homecare_area: {
                    ...homecare_area,
                    coords: newCoords,
                    address,
                    full_address,
                    label: homecare_area?.label || label || '',
                    radius,
                  }
                }));
              }}
            />
          </div>
        </div>

        <div className="flex justify-between gap-2 items-center pt-1">
          <LocateFixed
            className="text-primary cursor-pointer hover:animate-pulse flex-shrink-0"
            onClick={getCurrentUserLocation}
          />
          <div className="w-3/4 overflow-hidden">
            <p className="font-medium text-xs text-gray-800 truncate">
              {getAddressLabel(current_address, 'administrative_area_level_2')}, {getAddressLabel(current_address, 'administrative_area_level_1')}
            </p>
            <p className="text-xs text-gray-400 truncate">{getAddressLabel(current_address, 'route')}</p>
          </div>
          <Button disabled={!homecare_area?.label} onClick={updateHomecareAreaForm}>Terapkan</Button>
        </div>
      </div>
    </DialogContent>
  );
}

export default DialogLocationHomecare;
